<template>
    <div class="container mt-3">
        <div class="row">
            <div class="col-12">
                <div class="alert alert-success" v-if="saved">{{ $t('Turen din har blitt lagret') }}</div>
            </div>
            <div class="col-6">
                <a href="/practice" class="btn btn-light border"><i class="fas fa-arrow-left"></i> {{
                        $t('Tilbake')
                    }}</a>
            </div>
            <div class="col-6 text-end">
                <div @click="deleteRoute" class="btn btn-danger" :disabled="isDeleting">
                    <span class="fa fa-spin fa-spinner" v-show="isDeleting"></span>
                    <span v-show="!isDeleting">
            <i class="fas fa-trash"></i> {{ deleteConfirm ? $t('Er du sikker?') : $t('Slett') }}
          </span>
                </div>
            </div>
            <div class="col-12 mb-4 mt-2">
                <h2 class="fw-bold" v-html="isLoading ? $t('Laster...') : routeName"></h2>
                <h5 v-show="!isLoading">{{ routeCreatedAt }}</h5>
            </div>
        </div>
        <div class="row" v-show="!isLoading && !errorMessage">
            <div class="col-6 text-center">
                <div class="border-subtle py-2 mr-1 bg-primary-lighter text-white shadow">
                    <svg xmlns="http://www.w3.org/2000/svg" class="card-icon" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <br>{{ routeDuration }}
                </div>
            </div>
            <div class="col-6 text-center">
                <div class="border-subtle py-2 ml-1 bg-primary-lighter text-white shadow">
                    <svg viewBox="0 0 29 29" class="card-icon" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M16.9726 18.8534H11.2506M22.6947 18.8534H26.1279V15.2485C26.1287 14.9762 26.0323 14.7125 25.8561 14.5048C25.6799 14.2972 25.4354 14.1591 25.1666 14.1156L19.2615 13.1314L16.1716 9.0115C16.065 8.86937 15.9267 8.75401 15.7678 8.67455C15.6089 8.5951 15.4337 8.55374 15.256 8.55374H5.94763C5.52107 8.55077 5.10218 8.66706 4.7382 8.88949C4.37422 9.11192 4.07964 9.43164 3.88769 9.81258L2.97217 11.678C2.49168 12.6331 2.24088 13.6872 2.23975 14.7564V18.8534H5.52856"
                            stroke="white" stroke-width="2.28882" stroke-linecap="round" stroke-linejoin="round"/>
                        <path
                            d="M8.38971 22.2866C9.9698 22.2866 11.2507 21.0057 11.2507 19.4256C11.2507 17.8455 9.9698 16.5646 8.38971 16.5646C6.80961 16.5646 5.52869 17.8455 5.52869 19.4256C5.52869 21.0057 6.80961 22.2866 8.38971 22.2866Z"
                            stroke="white" stroke-width="2.28882" stroke-linecap="round" stroke-linejoin="round"/>
                        <path
                            d="M19.8336 22.2866C21.4137 22.2866 22.6946 21.0057 22.6946 19.4256C22.6946 17.8455 21.4137 16.5646 19.8336 16.5646C18.2535 16.5646 16.9725 17.8455 16.9725 19.4256C16.9725 21.0057 18.2535 22.2866 19.8336 22.2866Z"
                            stroke="white" stroke-width="2.28882" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <br>{{ routeDistance }} {{ $t('km') }}
                </div>
            </div>
            <div class="col-12 mt-4">
                <div ref="map" id="practice-drive-map"></div>
            </div>
        </div>

        <div class="row" v-if="!isLoading && errorMessage">
            <div class="alert alert-danger">
                {{ errorMessage }}
            </div>
        </div>

        <div class="row" v-show="isLoading">
            <div class="text-center col-12">
                <span class="spinner-border" role="status"></span>
            </div>
        </div>
    </div>
</template>

<script>
    // Load google map API https://developers.google.com/maps/documentation/javascript/load-maps-js-api#dynamic-library-import
    (g => {
        var h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary", q = "__ib__",
            m = document, b = window;
        b = b[c] || (b[c] = {});
        var d = b.maps || (b.maps = {}), r = new Set, e = new URLSearchParams,
            // oxlint-disable-next-line eslint/no-async-promise-executor
            u = () => h || (h = new Promise(async (f, n) => {
                await (a = m.createElement("script"));
                e.set("libraries", [...r] + "");
                for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]);
                e.set("callback", c + ".maps." + q);
                a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
                d[q] = f;
                a.onerror = () => h = n(Error(p + " could not load."));
                a.nonce = m.querySelector("script[nonce]")?.nonce || "";
                m.head.append(a)
            }));
        // oxlint-disable-next-line eslint/no-unused-expressions
        d[l] ? console.warn(p + " only loads once. Ignoring:", g) : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n))
    })({
        key: window.env.GOOGLE_MAPS_API_KEY,
        v: "weekly",
    });
</script>

<script setup>
    import {ref, computed, watch} from 'vue';
    import Store from '../../state/practiceDrivingRouteStore';

    const props = defineProps(['saved', 'routeid']);
    const emit = defineEmits(['deleteRoute']);

    const state = Store.state;
    const saved = ref(props.saved);
    const deleteConfirm = ref(false);
    const map = ref(null);

    Store.init(props.routeid);

    const initMap = () => {
        Store.initMap(map.value);
    };

    const deleteRoute = () => {
        if (!deleteConfirm.value) {
            deleteConfirm.value = true;
            return;
        }
        Store.deleteRoute();
        emit('deleteRoute');
    };

    const isLoading = computed(() => Store.isLoading() || Store.isMapLoading());
    const routeName = computed(() => {
        const name = Store.getRoute().name;
        if (!name) {
            return '<i>' + $t('Ikke navngitt') + '</i>';
        }
        return name;
    });
    const routeCreatedAt = computed(() => Store.getRoute().createdAt);
    const routeDistance = computed(() => Store.getRoute().distanceInKm);
    const routeDuration = computed(() => Store.getRoute().duration);
    const isDeleting = computed(() => Store.isDeleting());
    const errorMessage = computed(() => Store.getErrorMessage())

    watch(isLoading, (newVal) => {
        if (!newVal && !errorMessage.value) {
            initMap();
        }
    });
</script>

<style scoped>
    #practice-drive-map {
        width: 100%;
        max-height: calc(100vh - 250px);
        height: 500px;
        border: 1px solid #ddd;
        border-radius: 0.25rem;
    }

    .border-subtle {
        border: 1px solid rgba(0, 0, 0, 0.125);
        border-radius: 0.25rem;
    }
</style>